import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ACCEPTED_DOC_SLUGS, LEGAL_DOCS } from '@/lib/legalDocs';

/**
 * Does this user have an acceptance on record for the *current* version of
 * every document in the accepted set?
 *
 * Read through the user's own session — `legal_acceptances_select_own` scopes
 * the rows to `auth.uid()`. Cached for the session so sign-in is never taxed
 * with a repeat query; the answer only changes when the user accepts or when a
 * document version is bumped in `legalDocs.ts`.
 */
export const legalAcceptanceQueryKey = (userId: string | undefined) => [
  'legalAcceptanceStatus',
  userId,
  ACCEPTED_DOC_SLUGS.map((slug) => `${slug}@${LEGAL_DOCS[slug].version}`).join(','),
];

export const useLegalAcceptanceStatus = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: legalAcceptanceQueryKey(user?.id),
    enabled: !!user,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<{ accepted: boolean }> => {
      const { data, error } = await supabase
        .from('legal_acceptances')
        .select('doc_slug, doc_version')
        .eq('user_id', user!.id);

      // Never lock a user out of the product because of a read failure.
      if (error) {
        console.error('[legalAcceptance] status read failed:', error.message);
        return { accepted: true };
      }

      const have = new Set((data || []).map((r) => `${r.doc_slug}@${r.doc_version}`));
      const accepted = ACCEPTED_DOC_SLUGS.every((slug) =>
        have.has(`${slug}@${LEGAL_DOCS[slug].version}`),
      );
      return { accepted };
    },
  });
};